import { FC } from 'react';
import { UseFormRegister, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Colors } from '../../../../../assets';
import { Spacing } from '../../../../../assets/GlobalStyle/Variables';
import { RequestsFormValues } from '../../models';
import type { VirologyFormData } from '../../../../../api/labRequestsApi';

const p = 'virology.form_data' as const;

// ---------------------------------------------------------------------------
// Data types
// ---------------------------------------------------------------------------

// Sekcie s plochými boolean poľami (všetky okrem direct_antigen_detection)
type FlatSectionKey = Exclude<keyof VirologyFormData, 'direct_antigen_detection'>;

type SectionDef<K extends FlatSectionKey> = {
  sectionKey: K;
  title: string;
  fields: { key: keyof VirologyFormData[K] & string; label: string; sublabel?: string; block?: boolean }[];
};

// Helper — TypeScript odvodzuje K z prvého argumentu a validuje fields
function defineSection<K extends FlatSectionKey>(
  sectionKey: K,
  title: string,
  fields: { key: keyof VirologyFormData[K] & string; label: string, sublabel?: string, block?: boolean }[]
): SectionDef<K> {
  return { sectionKey, title, fields };
}

// Distributívny union — SectionDef<'bacterial_serology'> | SectionDef<'mycology'> | ...
type AnySectionDef = { [K in FlatSectionKey]: SectionDef<K> }[FlatSectionKey];

// Nested sekcia pre direct_antigen_detection
type DirectAntiGenSubKey = keyof VirologyFormData['direct_antigen_detection'];
type NestedSection<S extends DirectAntiGenSubKey> = {
  subKey: S;
  subTitle: string;
  fields: { key: keyof VirologyFormData['direct_antigen_detection'][S] & string; label: string }[];
};
type AnyNestedSection = { [S in DirectAntiGenSubKey]: NestedSection<S> }[DirectAntiGenSubKey];
type SectionWithSubsections = {
  sectionKey: 'direct_antigen_detection';
  title: string;
  subsections: AnyNestedSection[];
};

// ---------------------------------------------------------------------------
// Section data
// ---------------------------------------------------------------------------

const COLUMN_1: AnySectionDef[] = [
  defineSection('bacterial_serology', 'Bakteriální sérologie', [
      { key: 'tularemia_antibodies', label: 'Tularémie protilátky' },
      { key: 'brucellosis_antibodies', label: 'Brucelóza protilátky' },
      { key: 'toxoplasmosis_kfr_igg_igm_iga_ige', label: 'Toxoplasmóza KFR / IgG / IgM / IgA / IgE' },
      { key: 'yersinia_species_igg_iga_wb', label: 'Yersinia species – IgG / IgA (WB)' },
      { key: 'widal_salmonella_antibodies', label: 'WIDAL (protilátky proti salmonelám)' },
      { key: 'borreliosis_igg_igm_elisa_wb', label: 'Borrelióza IgG / IgM (ELISA, WB)' },
      { key: 'cxcl13_csf', label: 'CXCL13 (mozkomíšní mok)' },
      { key: 'antibody_index_csf_blood', label: 'AI - Antibody Index (mozkomíšní mok + krev)' },
  ]),
  defineSection('mycology', 'Mykologie', [
    { key: 'cryptococcus_antigenemia', label: 'Cryptococcus antigenémie' },
    { key: 'aspergillus_antigenemia', label: 'Aspergillus antigenémie' },
  ]),
  defineSection('mycobacteriology', 'Mykobakteriologie', [
    { key: 'quantiferon_tb', label: 'QuantiFERON TB (speciální odběrovky)' },
    { key: 't_spot_tb', label: 'T-SPOT TB (po domluvě)' },
  ]),
  defineSection('congenital_infections', 'Adnátní infekce', [
    { key: 'toxoplasma_gondii_kfr_igm_iga_igg', label: 'Toxoplasma gondii - KFR / IgM / IgA / IgG' },
    { key: 'rubella_virus', label: 'Virus rubeoly' },
    { key: 'herpetic_viruses', label: 'Herpetické viry' },
    { key: 'parvovirus_b19', label: 'Parvovirus B19' },
  ]),
  defineSection('childhood_exanthem_infections', 'Dětské a exantémové infekce', [
    { key: 'rubella_virus_igm_igg', label: 'Virus rubeoly- IgM / IgG' },
    { key: 'morbillivirus_igm_igg', label: 'Virus morbill - IgM / IgG' },
    { key: 'parvovirus_b19_igm_igg', label: 'Parvovirus B19 - IgM / IgG' },
    { key: 'mumps_virus_igm_igg', label: 'Virus parotitidy - IgM / IgG' },
  ]),
];

const COLUMN_2: AnySectionDef[] = [
  defineSection('traveler_infections', 'Infekce cestovatelů', [
    { key: 'chikungunya_virus_igm_igg', label: 'Virus Chikungunya – IgM / IgG' },
    { key: 'dengue_virus_igm_igg', label: 'Virus Dengue – IgM / IgG' },
    { key: 'dengue_virus_ns1_ag', label: 'Virus Dengue NS-1 Ag' },
    { key: 'zika_virus_igm_igg', label: 'Virus Zika – IgM / IgG' },
    { key: 'hantaviruses_igm_igg_typing', label: 'Hantaviry – IgM / IgG – typizace', sublabel: '(Puumala, Dobrava, Soul, Hantaan, Sin Nombre, Phleboviry: Sicilian, Toscana)' },
    { key: 'japanese_encephalitis_virus_igm_igg', label: 'Virus japonské encefalitidy – IgM / IgG' },
    { key: 'west_nile_virus_igm_igg', label: 'West Nile virus – IgM / IgG' },
  ]),
  defineSection('respiratory_infections', 'Respirační infekce', [
    { key: 'respiratory_block', label: 'Respirační blok', block: true },
    { key: 'influenza_a_b_kfr_igm', label: 'Viry influenzy A,B - KFR / IgM' },
    { key: 'adenoviruses_kfr_igm', label: 'Adenoviry - KFR / IgM' },
    { key: 'rsv_kfr_igm_iga', label: 'Respirační syncytiální virus RSV - KFR / IgM / IgA' },
    { key: 'mycoplasma_pneumoniae_kfr_igm_iga', label: 'Mycoplasma pneumoniae - KFR / IgM / IgA' },
    { key: 'parainfluenza_1_3_iga_igg', label: 'V. parainfluenzy 1-3 (PIV1-3) - IgA / IgG' },
    { key: 'legionella_pneumophila_igm_igg', label: 'Legionella pneumphila - IgM / IgG' },
    { key: 'bordetella_pertussis_iga_igg', label: 'Bordetella pertussis - IgA / IgG' },
    { key: 'bordetella_parapertussis_iga_igg', label: 'Bordetella parapertussis - IgA / IgG' },
    { key: 'sars_cov_2_blok', label: 'SARS-CoV-2', block: true },
    { key: 'sars_cov_2_antibodies_igm_iga', label: 'protilátky – IgM / IgA' },
    { key: 'sars_cov_2_spike_protein_igg', label: 'protilátky – Spike protein IgG' },
    { key: 'sars_cov_2_n_antigen_blood', label: 'N-antigen SARS-CoV-2 v krvi' },
    { key: 'sars_cov_2_antigen_nasopharyngeal_swab', label: 'antigen SARS-CoV-2 (výtěr z nosohltanu)' },
  ]),
  defineSection('atypical_pneumonia', 'Atypické pneumonie', [
    { key: 'mycoplasma_pneumoniae_kfr_igm_iga_igg', label: 'Mycoplasma pneumoniae - KFR / IgM / IgA / IgG' },
    { key: 'chlamydia_lps_igm_iga_igg', label: 'Chlamydia rodově společný LPS - IgM / IgA / IgG' },
    { key: 'chlamydia_pneumoniae_psittaci_igm_iga_igg', label: 'Chlamydia pneumoniae, psittaci - IgM / IgA / IgG' },
  ]),
];

const COLUMN_3: AnySectionDef[] = [
  defineSection('neuroinfections', 'Neuroinfekce', [
    { key: 'tick_borne_meningoencephalitis_virus', label: 'Virus klíšťové meningoencefalitidy', sublabel: '(krev - IgM / IgG, mozkomíšní mok - IgM)' },
    { key: 'enteroblock_kfr_igm_iga_igg', label: 'Enteroblok - KFR / IgM / IgA / IgG', sublabel: '(Enterovirus 68-71, Coxackie virus A,B, Echovirus)' },
    { key: 'neuroborreliosis_cxcl13', label: 'Neuroborelióza – CXCL13' },
    { key: 'neurosyphilis_cxcl13', label: 'Neurosyfilis – CXCL13' },
  ]),
  defineSection('herpetic_infections', 'Herpetické infekce', [
    { key: 'herpetic_block', label: 'Herpetický blok', block: true },
    { key: 'hsv_1_2_igm_igg_kfr', label: 'Herpes simplex T1, T2 (HSV - 1,2) - IgM / IgG / KFR' },
    { key: 'cmv_igm_igg_igg_avidity_kfr', label: 'Cytomegalovirus (CMV) - IgM / IgG /avidita IgG / KFR' },
    { key: 'vzv_igm_igg_kfr', label: 'Varicella zoster (VZV) - IgM / IgG / KFR' },
    { key: 'ebv_igm_igg_anti_ea_vca_ebna', label: 'EBV - IgM / IgG / anti EA / VCA / EBNA' },
    { key: 'hhv6_igm_igg', label: 'HHV6 - IgM / IgG' },
  ]),
  defineSection('rickettsiae', 'Ricketsie', [
    { key: 'bartonella_igm_igg', label: 'Bartonella - IgM / IgG' },
    { key: 'rickettsia_conorii_igm_igg', label: 'Rickettsia conorii - IgM / IgG' },
    { key: 'coxiella_burnetii_igm_iga_igg_kfr', label: 'Coxiella burnetii (Q-horečka) - IgM / IgA / IgG / KFR' },
  ]),
  defineSection('sexually_transmitted_infections', 'Pohlavně přenosné infekce', [
    { key: 'syphilis_serology_rpr_tppa', label: 'Sérologie syfilis – RPR /TPPA' },
    { key: 'hiv_1_2_p24_antigen', label: 'HIV1,2 a P24 antigen' },
    { key: 'chlamydia_trachomatis_igm_iga_igg', label: 'Chlamydia trachomatis - IgM / IgA / IgG' },
    { key: 'mycoplasma_hominis_igm_igg', label: 'Mycoplasma hominis - IgM / IgG' },
    { key: 'ureaplasma_urealyticum_igm_igg', label: 'Ureaplasma urealyticum - IgM / IgG' },
  ]),
];

const BOTTOM_SECTIONS: AnySectionDef[] = [
  defineSection('viral_hepatitis', 'Virové hepatitidy', [
    { key: 'hepatitis_panel_a_b_c', label: 'Panel hepatitid A,B,C', block: true },
    { key: 'hepatitis_a', label: 'Hepatitida A' },
    { key: 'anti_hav_igm', label: 'anti HAV IgM' },
    { key: 'anti_hav_ig_total', label: 'anti HAV Ig total' },
    { key: 'hepatitis_b', label: 'Hepatitida B' },
    { key: 'hepatitis_b_hbsag_quantitative', label: 'HBsAg kvantitativně' },
    { key: 'hepatitis_b_hbsag_confirmation', label: 'HBsAg konfirmace' },
    { key: 'hepatitis_b_hbeag_quantitative', label: 'HBeAg kvantitativně' },
    { key: 'hepatitis_b_anti_hbs_quantitative', label: 'anti HBs kvantitativně' },
    { key: 'hepatitis_b_anti_hbe_igg', label: 'anti HBe IgG' },
    { key: 'hepatitis_b_anti_hbc_ig_total', label: 'anti HBc Ig total' },
    { key: 'hepatitis_b_anti_hbc_igm', label: 'anti HBc IgM' },
    { key: 'hepatitis_c', label: 'Hepatitida C' },
    { key: 'anti_hcv_ig_total', label: 'anti HCV Ig total' },
    { key: 'anti_hcv_confirmation_wb', label: 'konfirmace metodou WB' },
    { key: 'hepatitis_e', label: 'Hepatitida E' },
    { key: 'anti_hev_igg_igm', label: 'anti HEV IgG / IgM' },
    { key: 'anti_hev_confirmation_wb', label: 'anti HEV konfirmace WB' },
  ]),
  defineSection('post_vaccination_igg_check', 'Kontrola IgG po očkování', [
    { key: 'tick_borne_encephalitis_virus_quantitative', label: 'V. klíšťové encefalitidy (kvant.)' },
    { key: 'poliovirus_1_2_3', label: 'Poliovirus 1,2,3' },
    { key: 'vzv', label: 'Varicella zooster virus (VZV)' },
    { key: 'rubella_virus_quantitative', label: 'Virus rubeoly (kvant.)' },
    { key: 'morbillivirus', label: 'Virus morbill' },
    { key: 'mumps_virus', label: 'Virus parotitidy' },
    { key: 'tetanus', label: 'Tetanus' },
    { key: 'hepatitis_b', label: 'Hepatitida B' },
    { key: 'sars_cov_2_spike_protein', label: 'SARS-CoV-2 Spike protein' },
  ]),
];

const DIRECT_ANTIGEN: SectionWithSubsections = {
  sectionKey: 'direct_antigen_detection',
  title: 'Přímý průkaz antigenu',
  subsections: [
    {
      subKey: 'respiratory_infections',
      subTitle: 'Respirační infekce',
      fields: [
        { key: 'direct_sars_cov_2_antigen_nasopharyngeal_swab', label: 'výtěr z nosohltanu na průkaz antigenu SARS-CoV-2' },
      ],
    },
    {
      subKey: 'git_infections_stool',
      subTitle: 'Infekce GIT (stolice)',
      fields: [
        { key: 'rotaviruses', label: 'rotaviry' },
        { key: 'adenoviruses', label: 'adenoviry' },
        { key: 'astroviruses', label: 'astroviry' },
        { key: 'noroviruses_genotype_1_2', label: 'noroviry (genotyp 1, 2)' },
        { key: 'enteroviruses', label: 'enteroviry' },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Helper component
// ---------------------------------------------------------------------------

const CheckSection: FC<{
  section: AnySectionDef;
  prefix: string;
  register: UseFormRegister<any>;
}> = ({ section, prefix, register }) => (
  <SectionBlock>
    <SectionTitle>{section.title}</SectionTitle>
    <Items>
      {section.fields.map((f) =>
        <CheckRow key={f.key}>
          <input
            type="checkbox"
            {...register(`${prefix}.${section.sectionKey}.${f.key}` as any)}
          />
          {f.block ? (
            <BlockTitle key={f.key}>{f.label}</BlockTitle>
          ) : (
            <ItemLabel>
              {f.label}
              <ItemSubLabel>{f.sublabel ? ` ${f.sublabel}` : ''}</ItemSubLabel>
            </ItemLabel>
          )}
        </CheckRow>
      )}
    </Items>
  </SectionBlock>
);

// ---------------------------------------------------------------------------
// Form component
// ---------------------------------------------------------------------------

export const VirologyForm: FC = () => {
  const { register } = useFormContext<RequestsFormValues>();

  return (
    <Wrapper>
      <MainGrid>
        <Column>
          {COLUMN_1.map((s) => <CheckSection key={s.sectionKey} section={s} prefix={p} register={register} />)}
        </Column>
        <Column>
          {COLUMN_2.map((s) => <CheckSection key={s.sectionKey} section={s} prefix={p} register={register} />)}
        </Column>
        <Column>
          {COLUMN_3.map((s) => <CheckSection key={s.sectionKey} section={s} prefix={p} register={register} />)}
        </Column>
      </MainGrid>

      <Hr />

      <BottomGrid>
        {BOTTOM_SECTIONS.map((s) => <CheckSection key={s.sectionKey} section={s} prefix={p} register={register} />)}

        <SectionBlock>
          <SectionTitle>{DIRECT_ANTIGEN.title}</SectionTitle>
          {DIRECT_ANTIGEN.subsections.map((sub) => (
            <div key={sub.subKey}>
              <SubSectionTitle>{sub.subTitle}</SubSectionTitle>
              <Items>
                {sub.fields.map((f) => (
                  <CheckRow key={f.key}>
                    <input
                      type="checkbox"
                      {...register(`${p}.${DIRECT_ANTIGEN.sectionKey}.${sub.subKey}.${f.key}` as any)}
                    />
                    <span>{f.label}</span>
                  </CheckRow>
                ))}
              </Items>
            </div>
          ))}
        </SectionBlock>
      </BottomGrid>
    </Wrapper>
  );
};

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(4)};
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(260px, 1fr));
  gap: ${Spacing(4)};

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, minmax(260px, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BottomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(260px, 1fr));
  gap: ${Spacing(4)};

  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, minmax(260px, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(4)};
`;

const SectionBlock = styled.section``;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: ${Spacing(4)};
  color: ${Colors.mainText};
`;

const SubSectionTitle = styled.div`
  font-weight: 600;
  margin: ${Spacing(1)} 0 ${Spacing(3)} 0;
  color: ${Colors.grey};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(1)};
`;

const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  user-select: none;
  cursor: pointer;

  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    cursor: pointer;
  }

  span {
    color: ${Colors.grey};
    font-size: 13px;
  }
`;

const BlockTitle = styled.div`
  font-weight: 600;
  color: ${Colors.grey};
  font-size: 12px;
  margin-top: ${Spacing(1)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ItemLabel = styled.span`
  font-size: 13px;
`;

const ItemSubLabel = styled.span`
  display: block;
  width: 100%;
  padding: ${Spacing(1)} 0 0 ${Spacing(3)};
  font-size: 11px !important;
  color: ${Colors.lightGrey};
`;

const Hr = styled.hr`
  border: none;
  border-top: 1px solid ${Colors.lightGrey};
  margin: 0;
`;
